import { useState, useEffect } from "react";
import "./profile.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import api, { setupInterceptor } from "../../utilities/Interceptor";
import { formatDate, getInitials } from "./utils/formatters";

import CabTab from "./components/CabTab";
import StatsTab from "./components/StatsTab";
import WalletTab from "./components/WalletTab";
import DownlineTab from "./components/DownlineTab";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [statsPeriod, setStatsPeriod] = useState("all");


  // add near your other downline state
  const [downline, setDownline] = useState([]);
  const [loadingDownline, setLoadingDownline] = useState(false);
  const [downlineFetched, setDownlineFetched] = useState(false);
  const [downlineRequestsById, setDownlineRequestsById] = useState({});
  const [expandedDriverId, setExpandedDriverId] = useState(null);

  const [profileHeader, setProfileHeader] = useState({
    avatarUrl: null,
    driver_ID: null,
    full_name: null,
    rating: null,
    verification: null,
    activation: null,
  });
  const [profile, setProfile] = useState({
    address: null,
    bio: null,
    contact: null,
    dob: null,
    driving_license: null,
    email: null,
    gender: null,
    service_type: [],
    referral_code: null,
  });
  const [cab, setCab] = useState({
    cab_id: null,
    cab_model: null,
    cab_number: null,
    medium_type: null,
    region_id: null,
    seat_capacity: null,
  });



  const [wallet, setWallet] = useState({ balance: null, upi_id: null });

  const [statsData, setStatsData] = useState({
    week: null,
    month: null,
    all: null,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  const [referralCopied, setReferralCopied] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const t = window.authToken || "12345";
      const rt = window.refreshToken || "12345";
      if (t && rt) {
        setToken(t);
        setRefreshToken(rt);
        clearInterval(interval);
        clearTimeout(timeout);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setLoading(false);
      setError("Authentication token not received.");
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (token && refreshToken) {
      setupInterceptor(token);
      localStorage.setItem("auth", JSON.stringify({ token, refreshToken }));
    }
  }, [token, refreshToken]);

  useEffect(() => {
    if (!token) return;

    setLoading(true);
    setError(null);

    const fetchDriverData = async () => {
      try {
        const [profileRes, statsRes] = await Promise.all([
          api.get("/driver/web/DPC"),
          api.get("/driver/web/stats?period=all"),
        ]);

        const { data } = profileRes;
        console.log("Profile Response:", profileRes.data);
        console.log("Stats Response:", statsRes.data);

        if (data.profileheader)
          setProfileHeader((p) => ({ ...p, ...data.profileheader }));
        if (data.profile) setProfile((p) => ({ ...p, ...data.profile }));
        if (data.cab) setCab((p) => ({ ...p, ...data.cab }));
        if (data.wallet) setWallet((p) => ({ ...p, ...data.wallet }));
        if (data.downline) setDownline(data.downline);

        if (statsRes.data.success) {
          setStatsData((prev) => ({ ...prev, all: statsRes.data.stats }));
        }
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [token]);

  useEffect(() => {
    if (!token || activeTab !== "stats") return;

    if (statsData[statsPeriod]) return;

    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        const { data } = await api.get(
          `/driver/web/stats?period=${statsPeriod}`,
        );
        if (data.success) {
          setStatsData((prev) => ({
            ...prev,
            [statsPeriod]: data.stats,
          }));
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
        setError("Failed to load statistics");
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [token, statsPeriod, activeTab, statsData]);

  const handleCopyReferral = () => {
    if (!profile.referral_code) return;
    navigator.clipboard?.writeText(profile.referral_code);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const isActive =
    profileHeader.activation === 1 ||
    String(profileHeader.activation).toLowerCase() === "active";
  const isVerified =
    profileHeader.verification === 1 ||
    String(profileHeader.verification).toLowerCase() === "verified";
  const displayAvatar = profileHeader.avatarUrl;
  const hasReferral = !!profile.referral_code;

  const currentStats = statsData[statsPeriod];
  const isLoadingStats = loadingStats || !currentStats;

  const pd = currentStats || {
    total_trips: 0,
    total_cancels: 0,
    total_earnings: 0,
    total_work_hours: 0,
    avg_rating: 0,
    chart_labels: [],
    chart_trips: [],
    chart_earnings: [],
  };

  useEffect(() => {
    if (!token || activeTab !== "downline") return;
    if (downlineFetched) return; // don't refetch every time the tab is clicked

    const driverId = profileHeader.driver_ID;
    const refCode = profile.referral_code;

    if (!driverId || !refCode) return; // wait until these are populated

    const fetchDownline = async () => {
      setLoadingDownline(true);
      try {
        const { data } = await api.get("/driver/web/downline", {
          params: {
            driver_id: driverId,
            ref_code: refCode,
          },
        });
        if (data.success) {

          console.log('in dw profile', data)

          setDownline(data.downline || []);
        }
        setDownlineFetched(true);
      } catch (err) {
        console.error("Error fetching downline:", err);
        setError("Failed to load downline");
      } finally {
        setLoadingDownline(false);
      }
    };

    fetchDownline();
  }, [token, activeTab, profileHeader.driver_ID, profile.referral_code, downlineFetched]);

  return (
    <div className="p-page">
      <div className={`p-card ${loading ? "p-card--loading" : ""}`}>
        {/* ── Avatar header ─────────────────────────────────────────────── */}
        <div className="p-header">
          <div className="p-header-pattern" aria-hidden="true" />

          <div
            className={`p-avatar-wrap ${hasReferral ? "p-avatar-wrap--referral" : ""}`}
          >
            {hasReferral && (
              <div className="p-avatar-ring" aria-hidden="true" />
            )}

            {loading ? (
              <div className="p-avatar p-avatar--skeleton skeleton" />
            ) : !isVerified ? (
              <div className="p-avatar p-avatar--unverified">
                <span>Get Verified</span>
              </div>
            ) : displayAvatar ? (
              <img
                className="p-avatar"
                src={displayAvatar}
                alt={profileHeader.full_name}
              />
            ) : (
              <div className="p-avatar p-avatar--initials">
                {getInitials(profileHeader.full_name)}
              </div>
            )}
          </div>
        </div>

        <div className="p-body">
          {error && <p className="p-error">{error}</p>}

          {/* ── Name / driver id / bio / badges ──────────────────────────── */}
          {loading ? (
            <>
              <div className="skeleton p-sk-name" />
              <div className="skeleton p-sk-id" />
              <div className="p-badge-row">
                <div className="skeleton p-sk-badge" />
                <div className="skeleton p-sk-badge" style={{ width: 80 }} />
              </div>
            </>
          ) : (
            <>
              <div className="p-header-conone">
                <p className="p-name">{profileHeader.full_name || "—"}</p>
                {hasReferral && (
                  <span className="p-badge p-badge--captain">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 2L3 7l9 5 9-5-9-5zM3 17l9 5 9-5M3 12l9 5 9-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                    Captain Driver
                  </span>
                )}
              </div>

              <p className="p-driver-id">
                Driver ID: {profileHeader.driver_ID || "—"}
              </p>

              {profile.bio && (
                <div className="p-detail-row" style={{ borderBottom: "none" }}>
                  <span className="p-detail-label">Bio</span>
                  <span className="p-detail-value p-detail-value--bio">
                    "{profile.bio}"
                  </span>
                </div>
              )}

              <div className="p-badge-row">
                <span
                  className={`p-badge ${isActive ? "p-badge--green" : "p-badge--neutral"}`}
                >
                  <svg
                    width="7"
                    height="7"
                    viewBox="0 0 8 8"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="4" cy="4" r="4" />
                  </svg>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <span
                  className={`p-badge ${isVerified ? "p-badge--blue" : "p-badge--neutral"}`}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {isVerified ? "Verified" : "Unverified"}
                </span>
                {profileHeader.rating > 0 && (
                  <span className="p-badge p-badge--amber">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="#EFB022"
                      stroke="#EFB022"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    {profileHeader.rating}
                  </span>
                )}
              </div>
            </>
          )}

          <hr className="p-divider" />

          {!loading && (
            <ProfileTabBar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isCaptain={hasReferral}
            />
          )}

          {/* ── Profile tab ───────────────────────────────────────────────── */}
          {activeTab === "profile" && !loading && (
            <div className="p-details" role="tabpanel">
              <DetailRow label="Email" value={profile.email} mono />
              <DetailRow label="Contact" value={profile.contact} />
              <DetailRow label="Gender" value={profile.gender} capitalize />
              <DetailRow
                label="Date of birth"
                value={formatDate(profile.dob)}
              />
              <DetailRow label="Address" value={profile.address} />
              <DetailRow label="License" value={profile.driving_license} mono />
              <DetailRow
                label="Service type"
                value={
                  Array.isArray(profile.service_type)
                    ? profile.service_type.join(", ")
                    : profile.service_type
                }
                last
              />

              {profile.referral_code && (
                <>
                  <div className="p-referral-divider">
                    <hr />
                    <span>Referral Code</span>
                    <hr />
                  </div>
                  <div className="p-referral-card">
                    <div className="p-referral-code-row">
                      <span className="p-referral-code">
                        {profile.referral_code}
                      </span>
                      <button
                        className={`p-referral-copy-btn ${copied ? "p-referral-copy-btn--copied" : ""}`}
                        onClick={() => copyToClipboard(profile.referral_code)}
                        aria-label="Copy referral code"
                      >
                        {copied ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "cab" && !loading && <CabTab cab={cab} />}

          {activeTab === "stats" && (
            <StatsTab
              statsPeriod={statsPeriod}
              setStatsPeriod={setStatsPeriod}
              statsData={statsData}
              isLoadingStats={isLoadingStats}
              pd={pd}
            />
          )}

          {activeTab === "wallet" && !loading && <WalletTab wallet={wallet} />}

          {activeTab === "downline" && !loading && (
            <DownlineTab
              downline={downline}
              loading={loadingDownline}
              refCode={profile.referral_code}
              requestsById={downlineRequestsById}
              setRequestsById={setDownlineRequestsById}
              expandedId={expandedDriverId}
              setExpandedId={setExpandedDriverId}
            />
          )}
          {loading && (
            <div className="p-details">
              {[...Array(6)].map((_, i) => (
                <div className="p-detail-row" key={i}>
                  <div
                    className="skeleton"
                    style={{ height: 10, width: "38%" }}
                  />
                  <div
                    className="skeleton"
                    style={{ height: 14, width: "50%" }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DetailRow({ label, value, mono, capitalize, last }) {
  return (
    <div
      className="p-detail-row"
      style={last ? { borderBottom: "none" } : undefined}
    >
      <span className="p-detail-label">{label}</span>
      <span
        className={`p-detail-value${mono ? " p-detail-value--mono" : ""}${capitalize ? " p-detail-value--cap" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

const TABS = ["profile", "cab", "stats", "wallet", "downline"];

function ProfileTabBar({ activeTab, setActiveTab, isCaptain }) {
  const visibleTabs = isCaptain
    ? TABS
    : TABS.filter((tab) => tab !== "downline");


  return (
    <div className="p-tab-bar" role="tablist">
      {visibleTabs.map((tab) => (
        <button
          key={tab}
          role="tab"
          aria-selected={activeTab === tab}
          className={`p-tab-btn ${activeTab === tab ? "p-tab-btn--active" : ""}`}
          onClick={() => setActiveTab(tab)}
        >
          {tab.charAt(0).toUpperCase() + tab.slice(1)}
        </button>
      ))}
    </div>
  );
}